import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
  LinearProgress,
} from "@mui/material";
import { observer } from "mobx-react";
import { JobRecord } from "../../../../../models/Jobs/JobRecord";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import { faDownload, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface JobRecordsTableProps {
  maxHeight?: number;
  data: JobRecord[];
  totalCount: number;
  onRefreshClick?: () => void;
  onDownloadClick?: () => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
}

const JobRecordsTable: React.FC<JobRecordsTableProps> = ({
  maxHeight = 600,
  data,
  totalCount,
  onRefreshClick,
  onDownloadClick,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  const theme = useTheme<Theme>();

  // Local state for pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>("start_time");
  const [sortType, setSortType] = useState<SortType>(SortType.DESC);
  const recordsPerPage = 50;

  const handleRowsChange = (value: number) => {
    setCurrentPage(1);
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setSortBy(column);
    setSortType(sortType);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return "-";
    return formatDate(date);
  };

  const getProgressDisplay = (progress: number) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: "60px", flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                backgroundColor:
                  progress === 100
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
              },
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ fontSize: "11px", minWidth: "35px" }}>
          {progress}%
        </Typography>
      </Box>
    );
  };

  const getDuration = (startTime: Date, endTime?: Date) => {
    return "In Progress";

    // const durationMs = endTime.getTime() - startTime.getTime();
    // const durationSeconds = Math.round(durationMs / 1000);

    // if (durationSeconds < 60) {
    //   return `${durationSeconds}s`;
    // } else if (durationSeconds < 3600) {
    //   return `${Math.round(durationSeconds / 60)}m`;
    // } else {
    //   return `${Math.round(durationSeconds / 3600)}h`;
    // }
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.customColors.lightGreyColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.customColors.blackBorder,
            fontSize: "1.1rem",
          }}
        >
          Job Records
        </Typography>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <button
            onClick={onRefreshClick}
            style={{
              padding: "10px 16px",
              color: theme.palette.customColors.purple,
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              transition: "all 0.2s ease",
            }}
          >
            <FontAwesomeIcon icon={faRefresh} fontSize="0.875rem" />
          </button>
          {onDownloadClick && (
            <button
              onClick={onDownloadClick}
              style={{
                padding: "10px 16px",
                backgroundColor: theme.palette.customColors.purple,
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.customColors.purple;
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  theme.palette.customColors.purple;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 6px rgba(0, 0, 0, 0.1)";
              }}
            >
              <FontAwesomeIcon icon={faDownload} fontSize="0.875rem" />
              Download Invoices
            </button>
          )}
        </Box>
      </Box>

      {data.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 5,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                      minWidth: "60px",
                    }}
                  >
                    S.No
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 60,
                      zIndex: 5,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      Job ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">Name</AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    <TableSortLabel
                      active={sortBy === "start_time"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "start_time",
                          sortBy === "start_time" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("start_time")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Start Time
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    End Time
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Duration
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    <TableSortLabel
                      active={sortBy === "progress"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        handleSortChange(
                          "progress",
                          sortBy === "progress" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("progress")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Progress
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Admin Name
                  </AppTableHeaderCell>
                  <AppTableHeaderCell minWidth="120px">
                    Summary
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow
                    key={`${row.id}-${index}`}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        textAlign: "center",
                        left: 0,
                        position: "sticky",
                        zIndex: 4,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        color: theme.palette.customColors.greyTextColor,
                        minWidth: "60px",
                      }}
                    >
                      {(currentPage - 1) * recordsPerPage + index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        textAlign: "center",
                        left: 60,
                        position: "sticky",
                        zIndex: 4,
                        boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 600,
                        color: theme.palette.customColors.purple,
                      }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateTime(row.start_time)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {formatDateTime(row.end_time ?? null)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      Duration
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {getProgressDisplay(row.progress)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.admin_name || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {row.summary || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > recordsPerPage && (
            <AppTablePagenation
              fontSize="12px"
              pageNo={currentPage}
              limit={recordsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              handleRowsChange={handleRowsChange}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No job records available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default observer(JobRecordsTable);
